import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Workspace } from '@robocorp/robocloud-internal-cds-sdk';
import { SideNavigation } from '@robocorp/components';
import {
  IconSettingsSliders,
  IconShield,
  IconBolt,
  IconPress,
  IconLayer2,
  IconCode,
  IconDatabase,
  IconUnorderedList,
} from '@robocorp/icons/iconic';
import { NavigationLink, RouterLink, WorkspaceRestriction } from '~/components';
import { useCurrentOrFirstOrganization } from '~/queries';

const ActivityMenuItem: FC<{ workspace: Workspace }> = ({ workspace }) => {
  const { t } = useTranslation();
  const canReadProcesses = workspace?.permissions?.includes('read_processes');
  const canReadAssistants = workspace?.permissions?.includes('read_assistants');

  if (canReadProcesses || canReadAssistants) {
    return (
      <NavigationLink
        component={SideNavigation.Accordion.Item}
        to={`/${workspace.org_short_name}/${workspace.short_name}/activity${
          !canReadProcesses ? '/assistantRuns' : ''
        }`}
        icon={<IconUnorderedList />}
        data-testid="workspace__nav-activity"
      >
        {t('activity.title')}
      </NavigationLink>
    );
  }

  return null;
};

const UnattendedMenuItem: FC<{ workspace: Workspace }> = ({ workspace }) => {
  const { t } = useTranslation();

  const { canReadProcesses, canReadWorkers } = useMemo(
    () => ({
      canReadProcesses: workspace?.permissions?.includes('read_processes'),
      canReadWorkers: workspace?.permissions?.includes('read_workers'),
    }),
    [workspace?.permissions],
  );

  const isProcessRoute = useMatch({
    path: `/:organizationShortName/${workspace?.short_name}/processes`,
    end: false,
  });

  const isWorkersRoute = useMatch({
    path: `/:organizationShortName/${workspace?.short_name}/workers`,
    end: false,
  });

  const isUnattendedRoute = isProcessRoute || isWorkersRoute;

  return canReadProcesses || canReadWorkers ? (
    <>
      <RouterLink
        data-testid="workspace__nav-unattended"
        component={SideNavigation.Accordion.Item}
        to={`/${workspace.org_short_name}/${workspace.short_name}/${
          canReadProcesses ? 'processes' : 'workers'
        }`}
        icon={<IconLayer2 />}
      >
        {t('unattended')}
      </RouterLink>
      {isUnattendedRoute && canReadProcesses && (
        <NavigationLink
          component={SideNavigation.Accordion.Item}
          to={`/${workspace.org_short_name}/${workspace.short_name}/processes`}
          data-testid="workspace__nav-processes"
          subItem
        >
          {t('processes')}
        </NavigationLink>
      )}
      {isUnattendedRoute && canReadWorkers && (
        <NavigationLink
          component={SideNavigation.Accordion.Item}
          to={`/${workspace.org_short_name}/${workspace.short_name}/workers`}
          data-testid="workspace__nav-environments"
          subItem
        >
          {t('workers.plural')}
        </NavigationLink>
      )}
    </>
  ) : null;
};

export const WorkspaceMenuItem: FC<{ workspace: Workspace }> = ({ workspace, ...rest }) => {
  const { t } = useTranslation();
  const { data: currentOrganization } = useCurrentOrFirstOrganization();

  const match = useMatch({
    path: `/:organizationShortName/${workspace.short_name}`,
    end: false,
  });

  const isAttendedRoute = useMatch({
    path: `/:organizationShortName/${workspace.short_name}/assistants`,
    end: false,
  });

  const isActive = !!match;
  const [isOpen, setIsOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) {
      setIsOpen(true);
    }
  }, [isActive]);

  const toggleAccordion = useCallback(() => setIsOpen((currentIsOpen) => !currentIsOpen), []);
  const isAssistantRole = workspace?.roleName === 'assistantUser';

  return (
    <SideNavigation.Accordion
      title={workspace.name}
      open={isOpen}
      onClick={toggleAccordion}
      data-testid="workspace__nav-accordion-title"
      identifier={workspace.id}
      {...rest}
    >
      <ActivityMenuItem workspace={workspace} />
      {isAssistantRole && (
        <NavigationLink
          component={SideNavigation.Accordion.Item}
          to={`/${workspace.org_short_name}/${workspace.short_name}`}
          icon={<IconPress />}
          end
        >
          {t('assistants')}
        </NavigationLink>
      )}
      {!isAssistantRole && (
        <>
          <UnattendedMenuItem workspace={workspace} />
          <WorkspaceRestriction workspace={workspace} permission="read_assistants">
            <RouterLink
              component={SideNavigation.Accordion.Item}
              to={`/${workspace.org_short_name}/${workspace.short_name}/assistants`}
              data-testid="workspace__nav-attended"
              icon={<IconPress />}
            >
              {t('attended')}
            </RouterLink>
            {isAttendedRoute && (
              <NavigationLink
                component={SideNavigation.Accordion.Item}
                to={`/${workspace.org_short_name}/${workspace.short_name}/assistants`}
                data-testid="workspace__nav-assistants"
                subItem
              >
                {t('assistants')}
              </NavigationLink>
            )}
          </WorkspaceRestriction>
          <WorkspaceRestriction workspace={workspace} permission="read_robots">
            <NavigationLink
              component={SideNavigation.Accordion.Item}
              to={`/${workspace.org_short_name}/${workspace.short_name}/tasks`}
              icon={<IconCode />}
              data-testid="workspace__nav-tasks"
            >
              {t('robot.taskPlural')}
            </NavigationLink>
          </WorkspaceRestriction>
          <WorkspaceRestriction workspace={workspace} permission="read_vault">
            <NavigationLink
              component={SideNavigation.Accordion.Item}
              to={`/${workspace.org_short_name}/${workspace.short_name}/vault`}
              icon={<IconShield />}
              data-testid="workspace__nav-vault"
            >
              {t('vault.title')}
            </NavigationLink>
          </WorkspaceRestriction>
          <WorkspaceRestriction workspace={workspace} permission="read_workspace_keys">
            <NavigationLink
              component={SideNavigation.Accordion.Item}
              to={`/${workspace.org_short_name}/${workspace.short_name}/api`}
              icon={<IconBolt />}
              data-testid="workspace__nav-api"
            >
              {t('integrations')}
            </NavigationLink>
          </WorkspaceRestriction>
          {currentOrganization?.limit_overrides.assetStorageEnabled && (
            <WorkspaceRestriction workspace={workspace} permission="read_asset_storage_assets">
              <NavigationLink
                component={SideNavigation.Accordion.Item}
                to={`/${workspace.org_short_name}/${workspace.short_name}/asset-storage`}
                icon={<IconDatabase />}
                data-testid="workspace__nav-asset-storage"
              >
                {t('assetStorage.title')}
              </NavigationLink>
            </WorkspaceRestriction>
          )}
        </>
      )}
      <NavigationLink
        component={SideNavigation.Accordion.Item}
        to={`/${workspace.org_short_name}/${workspace.short_name}/settings`}
        icon={<IconSettingsSliders />}
        data-testid="workspace__nav-settings"
      >
        {t('settingsAndMembers')}
      </NavigationLink>
    </SideNavigation.Accordion>
  );
};
